import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bot, Loader2, Zap, ArrowRight, CheckCircle2, Clock, MapPin, Inbox,
  MessageSquare, Star,
} from "lucide-react";
import { mmActions, mmApprove } from "@/lib/api";
import { toast } from "sonner";
import InfographicHero from "@/components/InfographicHero";

/** AI Marketing Manager — /marketing-manager */

const AREA_ICON = {
  inbox: Inbox, local: MapPin, reputation: Star, content: MessageSquare,
};
const PRIORITY_TONE = {
  high: "border-rose-200 bg-rose-50 text-rose-900",
  medium: "border-amber-200 bg-amber-50 text-amber-900",
  low: "border-slate-200 bg-slate-50 text-slate-800",
};

export default function MarketingManagerPage() {
  const [data, setData] = useState(null);
  const [approving, setApproving] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "AI Marketing Manager — WebsiteTech.pro";
    mmActions()
      .then(setData)
      .catch(() => setErr("Sign in to see your weekly action list."));
  }, []);

  const approve = async (key) => {
    setApproving({ ...approving, [key]: true });
    try {
      await mmApprove(key);
      toast.success("Action marked done");
    } catch (e) {
      toast.error("Could not save");
    } finally { setApproving({ ...approving, [key]: false }); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mm-page">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Bot className="h-4 w-4" /> AI Marketing Manager
          </div>
          <h1 className="mt-2 font-heading text-3xl font-black leading-tight text-slate-900 sm:text-5xl">
            One prioritised list. <span className="text-orange-600">Every Monday.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-slate-600 sm:text-base">
            The AI Marketing Manager scans your Local Visibility, unified inbox,
            reviews and content signals, then hands you the next best actions —
            in plain English, with a human strategist one click away.
          </p>
          <div className="mt-6">
            <InfographicHero slug="ai-marketing-manager" hint={false} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8">
        {err && (
          <Card className="border-amber-200 bg-amber-50 p-6 text-sm text-amber-900" data-testid="mm-err">
            {err} <Link to="/login" className="ml-2 font-semibold underline">Sign in</Link>
          </Card>
        )}
        {!data && !err && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Analysing your signals…
          </div>
        )}
        {data && (
          <>
            {/* signal summary */}
            <div className="mb-6 grid gap-3 sm:grid-cols-3" data-testid="mm-signals">
              <SignalTile label="Local Visibility Score" value={data.signals?.lv?.score ?? "—"}
                sub={data.signals?.lv ? `~${data.signals.lv.estimated_missed_leads_per_month} leads/mo missed` : "Run an audit"} />
              <SignalTile label="Unread leads" value={data.signals?.inbox_unread ?? 0}
                sub="reply within 30 min for 3× booking rate" />
              <SignalTile label="New pipeline" value={data.signals?.inbox_new ?? 0}
                sub="qualify within 24h" />
            </div>

            {/* action list */}
            <div className="space-y-3">
              {data.actions.map((a, idx) => {
                const Icon = AREA_ICON[a.area] || Zap;
                const key = `${a.area}-${idx}`;
                return (
                  <Card
                    key={key}
                    className={`border p-5 ${PRIORITY_TONE[a.priority]}`}
                    data-testid={`mm-action-${key}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="rounded-full border border-white bg-white/60 p-2">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-heading text-lg font-bold">{a.title}</div>
                            <Badge variant="outline" className="text-[10px] uppercase">
                              {a.priority}
                            </Badge>
                          </div>
                          <div className="mt-1 text-sm opacity-90">{a.why}</div>
                          <div className="mt-2 flex items-start gap-1 text-xs opacity-80">
                            <ArrowRight className="mt-0.5 h-3 w-3 shrink-0" />
                            <span>{a.next_step}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => approve(key)}
                        disabled={approving[key]}
                        className="border-white bg-white/80 hover:bg-white"
                        data-testid={`mm-action-approve-${key}`}
                      >
                        {approving[key]
                          ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
                        Mark done
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
              <Clock className="h-3 w-3" /> Next review: <b>{data.next_review}</b>
              · Generated {new Date(data.generated_at).toLocaleString()}
            </div>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}

function SignalTile({ label, value, sub }) {
  return (
    <Card className="border-slate-200 bg-white p-4">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-heading text-3xl font-black tabular-nums text-slate-900">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{sub}</div>
    </Card>
  );
}
